import { cn } from '@/lib/utils';

type LedNumberProps = {
  value: number | string;
  /** Total visible digits (left-padded with spaces, so DSEG7 renders blanks). */
  digits?: number;
  /** Render as currency: "$ 1,304" instead of "1304". */
  currency?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  pulse?: boolean;
  className?: string;
  ariaLabel?: string;
};

const sizeMap = {
  sm: 'text-2xl',
  md: 'text-4xl',
  lg: 'text-6xl',
  xl: 'text-7xl md:text-8xl',
};

/**
 * Red 7-segment LED-style number display. Uses self-hosted DSEG7-Classic.
 * Renders lit digits only — no fake "dim segment" ghost overlay; DSEG7
 * already gives you the LED look.
 */
export function LedNumber({
  value,
  digits = 6,
  currency = false,
  size = 'lg',
  pulse = false,
  className,
  ariaLabel,
}: LedNumberProps) {
  const raw = typeof value === 'number' ? value.toLocaleString('en-US') : value;
  const display = currency ? `$ ${raw}` : raw;
  const padded = display.padStart(digits, ' ');

  return (
    <span
      role="status"
      aria-label={ariaLabel ?? `value ${display}`}
      className={cn(
        'inline-block rounded-md bg-black/85 px-4 py-2 font-led led-display tabular-nums whitespace-pre',
        'ring-1 ring-black/80 shadow-inner shadow-black/80',
        pulse && 'animate-led-pulse',
        sizeMap[size],
        className,
      )}
    >
      {padded}
    </span>
  );
}
